/**
 * USERS, AND WHAT EACH OF THEM MAY ACTUALLY DO.
 *
 * The question this screen exists to answer is not "what roles does he hold" —
 * it is "what may he do", which since 0044 is the union of his own grants and
 * every role he holds, and therefore has a WRONG answer available to anyone who
 * looks in one table. `users.get` reads `platform.effective_functions()`, the
 * same one place the permission check itself reads, and says of each permission
 * whether it is his own, comes from a role, or both. A manager taking a role off
 * somebody can see here whether that actually removes anything.
 *
 * The password reset is a manager's act and never shows the person's old
 * password, because there isn't one to show: the application holds a hash and
 * the door hashes the temporary password before the command ever sees it
 * (DEF-081).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, Register, Select, Text, TextArea, Check, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { count, words, instant } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('users')!.messages;

export function Users({ id, session }: { id: string | null; session: Session }) {
  if (id) return <OneUser userId={Number(id)} session={session} />;
  return <UserRegister session={session} />;
}

/** 0172 — DEF-324: a manager adds a user from the application; the password travels once, as a temporary one. */
function AddUser({ onDone }: { onDone: (userId: number | null) => void }) {
  const [username, setUsername] = useState(''); const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState(''); const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function add() {
    let made: number | null = null;
    const ok = await runner.run(async () => {
      const r = await api.command('user.create', { username: username.trim(), displayName: displayName.trim(), temporaryPassword: password, reason: reason.trim() });
      made = (r.value as { userId?: number })?.userId ?? null;
      return r;
    });
    if (ok) onDone(made);
  }
  return (
    <Card title="Add a user" sub="they sign in with the temporary password and change it; what they may do comes from the roles you give them next">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Text name="new-username" label="Username" value={username} onChange={setUsername} required help="6 to 64 characters, no spaces — what they type to sign in" />
      <Text name="new-name" label="Name" value={displayName} onChange={setDisplayName} required />
      <Text name="new-password" label="Temporary password" type="password" value={password} onChange={setPassword} required help="at least 6 characters; they change it at their first sign-in" />
      <Text name="new-reason" label="Why (optional)" value={reason} onChange={setReason} help="a note for the record, if it helps somebody later — their job, or who asked for the account" />
      <Button kind="primary" onClick={() => { void add(); }} disabled={runner.busy || username.trim().length < 6 || !displayName.trim() || password.length < 6 || !reason.trim()}>
        {runner.busy ? 'Adding…' : 'Add the user'}
      </Button>
    </Card>
  );
}

function UserRegister({ session }: { session: Session }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('users.list', {
    ...(q.trim() ? { q: q.trim() } : {}), ...(onlyActive ? { active: true } : {}), page, pageSize: 50,
  }), [q, onlyActive, page], MESSAGES);

  const columns: readonly Column<{ userId: number; username: string; displayName: string | null; kind: string; active: boolean; roles: string[]; branches: string[]; defaultBranch: string | null }>[] = [
    { key: 'username', head: 'Username', cell: (r) => <strong>{r.username}</strong> },
    { key: 'name', head: 'Name', cell: (r) => r.displayName ?? <span className="muted">—</span> },
    { key: 'kind', head: 'Kind', cell: (r) => <Pill>{r.kind}</Pill> },
    { key: 'roles', head: 'Roles', cell: (r) => r.roles.length ? r.roles.join(', ') : <span className="muted">none — only their own grants</span> },
    // 14 Oct — WHERE THEY WORK, in the list. A branch manager settles only what arrived at
    // a branch they hold, so "which branch is he" is the first question about any person,
    // and it used to cost one page-open per head. The default is marked, because that is
    // the one their forms start on.
    { key: 'branches', head: 'Branch', cell: (r) => r.branches.length === 0
        ? <span className="muted">whole company</span>
        : <>{r.branches.map((b) => (
            <Pill key={b} tone={b === r.defaultBranch ? 'good' : 'muted'}>{b}</Pill>))}</> },
    { key: 'active', head: 'Status', cell: (r) => <Pill tone={r.active ? 'good' : 'muted'}>{r.active ? 'active' : 'disabled'}</Pill> },
  ];

  return (
    <Page title="Users" sub="Who can sign in, and what each of them may do."
          actions={<>
            {can(session, 'user.create') ? <Button kind="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add a user'}</Button> : null}
            {can(session, 'role.manage') ? <Button onClick={() => go('#/roles')}>Roles</Button> : null}
          </>}>
      {adding ? <AddUser onDone={(id) => { setAdding(false); asked.reload(); if (id) go(`#/users/${id}`); }} /> : null}
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Username or name" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Check name="only-active" checked={onlyActive} onChange={setOnlyActive} label="Only people who can sign in" />
      </div>
      <Card>
        {asked.loading ? <Loading what="users" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No user matches that."
                        onOpen={(r) => go(`#/users/${r.userId}`)}
 asOf={instant(new Date().toISOString())} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

function OneUser({ userId, session }: { userId: number; session: Session }) {
  const asked = useAsk(() => api.view('users.get', { userId }), [userId], MESSAGES);
  const roles = useAsk(() => api.view('roles.list', { active: true }), [], MESSAGES);
  // audit G1 — what each permission LETS them do; read before the early
  // returns below, because a hook may not be called conditionally
  const catalogue = useAsk(() => api.view('functions.list', {}), [], MESSAGES);
  const meaning = new Map((catalogue.data?.rows ?? []).map((r) => [r.function, r]));
  const runner = useRun(MESSAGES);
  const [resetting, setResetting] = useState(false);
  const [addRole, setAddRole] = useState('');
  const branches = useAsk(() => api.view('branches.list', { active: true }), [], MESSAGES);

  if (asked.loading) return <Page title="User"><Loading what="the user" /></Page>;
  if (asked.error) return <Page title="User"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const may = can(session, 'role.manage');

  async function assign() {
    const ok = await runner.run(() => api.command('role.assign', { userId, roleId: Number(addRole) }));
    if (ok) { setAddRole(''); asked.reload(); }
  }

  return (
    <Page title={d.user.displayName ?? d.user.username} sub={`${d.user.username} · ${words(d.user.kind)}`}
          actions={<>
            <Pill tone={d.user.active ? 'good' : 'muted'}>{d.user.active ? 'can sign in' : 'disabled'}</Pill>
            {can(session, 'user.reset_password') ? <Button onClick={() => setResetting((v) => !v)}>{resetting ? 'Leave the password' : 'Reset the password'}</Button> : null}
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
      {resetting ? <ResetPassword userId={userId} username={d.user.username} onDone={() => { setResetting(false); asked.reload(); }} /> : null}

      <div className="grid two">
        <Card title="Roles they hold" sub="a role is a bundle of permissions — taking one off may remove nothing, if they also have the permission directly">
          {d.user.roles.length === 0 ? <p className="muted">They hold no role. Everything below is granted to them directly.</p> : (
            <div>
              {d.user.roles.map((code) => {
                const role = roles.data?.rows.find((r) => r.code === code);
                return (
                  <div className="queue-item" key={code}>
                    <span className="grow"><strong>{code}</strong>{role ? <span className="muted"> · {role.name}</span> : null}</span>
                    {may && role ? <UnassignRole userId={userId} roleId={role.roleId} code={code} onDone={() => asked.reload()} /> : null}
                  </div>
                );
              })}
            </div>
          )}
          {may ? (
            <div className="toolbar" style={{ marginTop: 14 }}>
              <Select name="add-role" label="Give them a role" value={addRole} onChange={setAddRole} placeholder="Choose a role"
                      options={(roles.data?.rows ?? []).filter((r) => !d.user.roles.includes(r.code)).map((r) => ({ value: String(r.roleId), label: `${r.code} — ${r.name}` }))} />
              <Button kind="primary" small onClick={() => { void assign(); }} disabled={!addRole || runner.busy}>Give it to them</Button>
            </div>
          ) : null}
        </Card>

        {/*
          * TEN-2 — WHERE their approvals count. A branch manager settles only identities
          * that arrived at a branch they work at, so this sits beside the roles rather
          * than on a separate page: assigning BRANCH_MANAGER without saying which yards
          * is what made that authority company-wide in the first place.
          */}
        <Card title="Branches they work at" sub="a branch manager approves only what arrived at one of these — the default is where their forms start">
          {d.branches.length === 0
            ? <Banner kind="info" title="They are assigned to no branch."
                      detail="Their work is recorded without a branch, and any branch-scoped approval they hold reaches the whole company. Give them a branch to narrow it." />
            : <div>
                {d.branches.map((br) => (
                  <div className="queue-item" key={br.branchId}>
                    <span className="grow"><strong>{br.code}</strong><span className="muted"> · {br.name} · {words(br.kind)}</span></span>
                    {br.isDefault ? <Pill tone="primary">their default</Pill> : null}
                    {br.active ? null : <Pill tone="muted">closed</Pill>}
                  </div>
                ))}
              </div>}
          {can(session, 'platform.assign_user_branch')
            ? <AssignBranch userId={userId}
                            options={(branches.data?.rows ?? []).map((br) => ({ value: String(br.branchId), label: `${br.code} — ${br.name}` }))}
                            onDone={() => asked.reload()} />
            : null}
        </Card>

        <Card title="What they may actually do" sub="the union of their own grants and every role they hold — the same reading the permission check makes">
          {d.functions.length === 0 ? (
            <Banner kind="warn" title="They may do nothing at all." detail="They can sign in and will be refused everywhere. Give them a role, or grant permissions directly." />
          ) : (
            <Register
              columns={[
                { key: 'fn', head: 'Permission', cell: (f: { function: string }) => {
                    const m = meaning.get(f.function);
                    return <>
                      <span>{m?.description ?? f.function}</span>
                      {m?.movesMoney ? <Pill tone="testing">moves money or custody</Pill> : null}
                      <div className="muted"><code>{f.function}</code>{m ? ` \u00b7 ${words(m.module)}` : ''}</div>
                    </>;
                  } },
                { key: 'from', head: 'Where it comes from', cell: (f: { direct: boolean; fromRoles: string[] }) => (
                  <>
                    {f.direct ? <Pill tone="primary">their own</Pill> : null}
                    {f.fromRoles.length ? <> {f.fromRoles.map((r) => <Pill key={r}>{r}</Pill>)}</> : null}
                    {f.direct && f.fromRoles.length ? <div className="muted">both — removing one leaves the other</div> : null}
                  </>
                ) },
              ]}
              rows={d.functions} empty="none"
              totals={<span><b>{count(d.functions.length)}</b> permission(s)</span>} />
          )}
        </Card>
      </div>
    </Page>
  );
}

function UnassignRole({ userId, roleId, code, onDone }: { userId: number; roleId: number; code: string; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('role.unassign', { userId, roleId, reason: reason.trim() }));
    if (ok) { setAsking(false); onDone(); }
  }
  if (!asking) return <Button small kind="danger" onClick={() => setAsking(true)}>Take it off</Button>;
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input id={`why-${code}`} type="text" placeholder="Why?" value={reason}
             onChange={(e: { target: { value: string } }) => setReason(e.target.value)} />
      <Button small kind="danger" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>Confirm</Button>
      <Button small onClick={() => setAsking(false)}>Keep it</Button>
    </span>
  );
}

function ResetPassword({ userId, username, onDone }: { userId: number; username: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('user.resetPassword', { userId, temporaryPassword: password, reason: reason.trim() }));
    if (ok) { setPassword(''); setReason(''); onDone(); }
  }
  return (
    <Card title={`Reset the password for ${username}`} sub="they must change it themselves at the next sign-in">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="Nobody, including you, can read their current password."
              detail="The password is never shown again. This sets a temporary one, which you must tell them by some other means than this screen." />
      <Text name="temp-password" label="Temporary password" value={password} onChange={setPassword} required />
      <TextArea name="reset-reason" label="Why it is being reset" value={reason} onChange={setReason} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !password || !reason.trim()}>
        {runner.busy ? 'Resetting…' : 'Set the temporary password'}
      </Button>
    </Card>
  );
}

/**
 * GIVING SOMEBODY AUTHORITY AT A YARD (TEN-2).
 *
 * `makeDefault` is a deliberate second decision rather than implied: adding a branch a
 * supervisor is covering this week should not move the branch his forms start on, and
 * the person doing the assigning is the only one who knows which of the two they mean.
 */
function AssignBranch({ userId, options, onDone }: {
  userId: number; options: readonly { value: string; label: string }[]; onDone: () => void;
}) {
  const [branchId, setBranchId] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('platform.assignUserBranch', {
      userId, branchId: Number(branchId), makeDefault, reason: reason.trim(),
    }));
    if (ok) { setBranchId(''); setMakeDefault(false); setReason(''); onDone(); }
  }
  return (
    <div style={{ marginTop: 14 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Select name="add-branch" label="Give them authority at a branch" value={branchId} onChange={setBranchId}
              placeholder="Choose a branch" options={options} />
      <Check name="make-default" checked={makeDefault} onChange={setMakeDefault}
             label="Also make it the branch their forms start on" />
      <Text name="branch-reason" label="Why" value={reason} onChange={setReason} required />
      <Button kind="primary" small onClick={() => { void submit(); }}
              disabled={runner.busy || !branchId || !reason.trim()}>
        {runner.busy ? 'Assigning…' : 'Give them the branch'}
      </Button>
    </div>
  );
}
